import { Plus, Pencil, Trash2, Loader } from "lucide-react";
import { useEffect, useState } from "react";
import Button from "shared/Button";
import PackageFormDialog, { PackageFormData } from "./PackageFormDialog";
import { iPackageSize } from "core/kangarooTypes/iPackageSize";
import { getPackagesSize } from "core/Services/CommonServices";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  addPackage,
  updatePackage,
  deletePackage,
} from "core/Services/AdminServices";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const PackageSizeManagement = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [packageSizes, setPackageSizes] = useState<iPackageSize[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<iPackageSize | null>(
    null
  );
  const [deletingPackageId, setDeletingPackageId] = useState<number | null>(
    null
  );

  const handleAddPackage = () => {
    setEditingPackage(null);
    setShowModal(true);
  };

  const handleEditPackage = (pkg: iPackageSize) => {
    setEditingPackage(pkg);
    setShowModal(true);
  };

  const handleDeletePackage = async (id: number) => {
    try {
      setDeletingPackageId(id);
      await deletePackage(id);
      fetchData();
      toast.success(t("packageSizeManagement.deleteSuccess"));
    } catch (error) {
      toast.error(t("packageSizeManagement.deleteError"));
    } finally {
      setDeletingPackageId(null);
    }
  };

  const handleSubmit = async (data: PackageFormData) => {
    try {
      if (editingPackage) {
        await updatePackage(data, editingPackage.id);
        toast.success(t("packageSizeManagement.updateSuccess"));
      } else {
        await addPackage(data);
        toast.success(t("packageSizeManagement.addSuccess"));
      }
      setShowModal(false);
      await fetchData();
    } catch (error) {
      toast.error(t("packageSizeManagement.saveError"));
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await getPackagesSize();
      setPackageSizes(response.data);
    } catch (error) {
      toast.error(t("packageSizeManagement.fetchError"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const PackageSizeTableSkeleton = () => {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                {t("packageSizeManagement.id")}
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                {t("packageSizeManagement.name")}
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                {t("packageSizeManagement.description")}
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                {t("packageSizeManagement.price")}
              </th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                {t("packageSizeManagement.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {[...Array(5)].map((_, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <Skeleton width={30} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Skeleton width={120} />
                </td>
                <td className="px-6 py-4">
                  <Skeleton width={200} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Skeleton width={80} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex justify-end gap-2">
                    <Skeleton width={20} height={20} circle />
                    <Skeleton width={20} height={20} circle />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div
        className="flex flex-col gap-2 md:flex-row md:justify-between 
      mb-6 w-full"
      >
        <h2 className="text-xl font-medium text-gray-900">
          {t("packageSizeManagement.title")}
        </h2>
        <Button onClick={handleAddPackage} icon={<Plus size={16} />}>
          {t("packageSizeManagement.addNew")}
        </Button>
      </div>

      {isLoading ? (
        <PackageSizeTableSkeleton />
      ) : packageSizes.length === 0 ? (
        <div className="p-4 border border-yellow-300 bg-yellow-50 rounded-md text-yellow-800">
          {t("packageSizeManagement.noPackages")}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                  {t("packageSizeManagement.id")}
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                  {t("packageSizeManagement.name")}
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                  {t("packageSizeManagement.description")}
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                  {t("packageSizeManagement.price")}
                </th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                  {t("packageSizeManagement.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {packageSizes.map((pkg) => (
                <tr key={pkg.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {pkg.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {pkg.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {pkg.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    ${pkg.price.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => handleEditPackage(pkg)}
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                      <button
                        className={`text-red-600 hover:text-red-900 ${
                          deletingPackageId === pkg.id
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        onClick={() => handleDeletePackage(pkg.id)}
                        disabled={deletingPackageId === pkg.id}
                      >
                        {deletingPackageId === pkg.id ? (
                          <Loader className="h-5 w-5 animate-spin" />
                        ) : (
                          <Trash2 className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Package Form Dialog */}
      <PackageFormDialog
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        initialData={editingPackage || undefined}
        isEditing={!!editingPackage}
      />
    </div>
  );
};

export default PackageSizeManagement;
